/**
 * Barcode/QR Code Parser Utility
 * Parses scanned QR codes with prefixes: BATCH:, LOC:, PROD:
 */

export type BarcodeType = "BATCH" | "LOC" | "PROD" | "UNKNOWN";

export interface ParsedBarcode {
  type: BarcodeType;
  identifier: string;
  raw: string;
  isValid: boolean;
  components?: Record<string, string>;
}

const BARCODE_PREFIXES: Record<Exclude<BarcodeType, "UNKNOWN">, string> = {
  BATCH: "BATCH:",
  LOC: "LOC:",
  PROD: "PROD:",
};

/**
 * Validates identifier based on type
 */
const validateIdentifier = (type: BarcodeType, identifier: string): boolean => {
  if (!identifier || identifier.length === 0) return false;

  switch (type) {
    case "BATCH":
      // Batch format: {PRODUCT_CODE}-{MMDD}-{MMDD}
      // e.g., ACEITE500-0106-0110
      return /^[A-Za-z0-9]+-\d{4}-\d{4}$/.test(identifier);

    case "LOC":
      // Location format: {WAREHOUSE}-{HALLWAY}-{RACK}-{LEVEL}-{POSITION}
      // e.g., BOD-001-HA-R01-L2-P03
      // More flexible: at least warehouse code
      return /^[A-Za-z0-9-]+$/.test(identifier) && identifier.length >= 3;

    case "PROD":
      // Product format: {SKU}:{LOT_NUMBER}
      // e.g., AOL-EV-500:LOT-2026-0106-001
      return identifier.includes(":") && identifier.split(":").length === 2;

    default:
      return false;
  }
};

/**
 * Parse type-specific components from identifier
 */
const parseComponents = (
  type: BarcodeType,
  identifier: string
): Record<string, string> | undefined => {
  switch (type) {
    case "LOC": {
      // BOD-001-HA-R01-L2-P03 → { warehouse, hallway, rack, level, position }
      const parts = identifier.split("-");
      if (parts.length >= 2) {
        const result: Record<string, string> = {
          warehouseCode: parts.slice(0, 2).join("-"), // BOD-001
        };
        if (parts.length >= 3) result.hallway = parts[2]; // HA
        if (parts.length >= 4) result.rack = parts[3]; // R01
        if (parts.length >= 5) result.level = parts[4]; // L2
        if (parts.length >= 6) result.position = parts[5]; // P03
        result.fullCode = identifier;
        return result;
      }
      return { fullCode: identifier };
    }

    case "PROD": {
      // AOL-EV-500:LOT-2026-0106-001 → { sku, lot }
      const [sku, lot] = identifier.split(":");
      return { sku, lot };
    }

    case "BATCH": {
      // ACEITE500-0106-0110 → { productCode, startMmdd, endMmdd }
      const match = identifier.match(/^(.+)-(\d{4})-(\d{4})$/);
      if (match) {
        return {
          productCode: match[1],
          startMmdd: match[2],
          endMmdd: match[3],
        };
      }
      return { batchNumber: identifier };
    }

    default:
      return undefined;
  }
};

/**
 * Parse a raw barcode string into structured data
 */
export const parseBarcode = (raw: string): ParsedBarcode => {
  const trimmed = raw.trim();

  // Try to match each known prefix
  for (const [type, prefix] of Object.entries(BARCODE_PREFIXES)) {
    if (trimmed.toUpperCase().startsWith(prefix)) {
      const identifier = trimmed.slice(prefix.length);
      const barcodeType = type as BarcodeType;
      const isValid = validateIdentifier(barcodeType, identifier);

      return {
        type: barcodeType,
        identifier,
        raw: trimmed,
        isValid,
        components: isValid
          ? parseComponents(barcodeType, identifier)
          : undefined,
      };
    }
  }

  // Unknown format
  return {
    type: "UNKNOWN",
    identifier: trimmed,
    raw: trimmed,
    isValid: false,
  };
};

/**
 * Generate a barcode value for a batch (no prefix, just the batch number)
 */
export const generateBatchQrCode = (batchNumber: string): string => {
  return batchNumber;
};

/**
 * Generate a barcode value for a location (no prefix, just the code)
 */
export const generateLocationQrCode = (fullCode: string): string => {
  return fullCode;
};

/**
 * Generate a QR code value for a product with lot
 */
export const generateProductQrCode = (
  sku: string,
  lotNumber: string
): string => {
  return `PROD:${sku}:${lotNumber}`;
};

/**
 * Get display label for barcode type
 */
export const getBarcodeTypeLabel = (type: BarcodeType): string => {
  const labels: Record<BarcodeType, string> = {
    BATCH: "Lote de Producción",
    LOC: "Ubicación",
    PROD: "Producto",
    UNKNOWN: "Desconocido",
  };
  return labels[type];
};
